import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { useState, FormEvent } from "react";

// ─── Replace this with your Formspree endpoint ───────────────────────────────
// 1. Go to https://formspree.io and sign in with the email where you want
//    to receive messages.
// 2. Click "New Form", give it a name (e.g. "Mychromamind Contact").
// 3. Copy the endpoint — it looks like: https://formspree.io/f/xyzabcde
// 4. Paste it below.
const FORMSPREE_ENDPOINT = import.meta.env.VITE_FORMSPREE_ENDPOINT ?? "";
// ─────────────────────────────────────────────────────────────────────────────

type Status = "idle" | "sending" | "success" | "error";

export default function Contact() {
  useSeo({
    title: "Contact | Mychromamind",
    description:
      "Get in touch with Mychromamind — questions, feedback, or partnership enquiries.",
    path: "/contact",
  });

  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!FORMSPREE_ENDPOINT) {
      setStatus("error");
      setErrorMsg("Contact form is not configured yet. Please try again later.");
      return;
    }

    setStatus("sending");
    const form = e.currentTarget;
    const data = new FormData(form);

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });

      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        const json = await res.json().catch(() => ({}));
        setErrorMsg(
          (json as { error?: string }).error ??
            "Something went wrong. Please try again."
        );
        setStatus("error");
      }
    } catch {
      setErrorMsg("Network error — please check your connection and try again.");
      setStatus("error");
    }
  }

  return (
    <Shell>
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-24 w-full">
        <h1 className="text-4xl md:text-5xl font-serif mb-4">Get in touch</h1>
        <p className="text-muted-foreground text-lg mb-12">
          Whether you have a question, spotted something wrong, or just want to
          say hello — we'd love to hear from you.
        </p>

        {status === "success" ? (
          <div className="p-8 rounded-2xl border border-border bg-card text-center space-y-3">
            <div className="text-4xl">✉️</div>
            <h2 className="text-xl font-serif">Message sent!</h2>
            <p className="text-muted-foreground text-sm">
              Thanks for reaching out. We aim to reply within 3–5 business days.
            </p>
            <button
              onClick={() => setStatus("idle")}
              className="mt-2 text-sm text-primary underline underline-offset-4 hover:opacity-80 transition-opacity"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-5"
            noValidate
          >
            {/* Name */}
            <div className="space-y-1.5">
              <label
                htmlFor="name"
                className="text-sm font-medium text-foreground"
              >
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                autoComplete="name"
                placeholder="Your name"
                className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow"
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="text-sm font-medium text-foreground"
              >
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow"
              />
            </div>

            {/* Subject */}
            <div className="space-y-1.5">
              <label
                htmlFor="subject"
                className="text-sm font-medium text-foreground"
              >
                Subject
              </label>
              <input
                id="subject"
                name="subject"
                type="text"
                required
                placeholder="What's this about?"
                className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow"
              />
            </div>

            {/* Message */}
            <div className="space-y-1.5">
              <label
                htmlFor="message"
                className="text-sm font-medium text-foreground"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                placeholder="Tell us what's on your mind…"
                className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-shadow resize-none"
              />
            </div>

            {/* Error */}
            {status === "error" && (
              <p
                role="alert"
                className="text-sm text-destructive bg-destructive/10 rounded-xl px-4 py-3"
              >
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full sm:w-auto px-8 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === "sending" ? "Sending…" : "Send message"}
            </button>
          </form>
        )}

        <p className="mt-10 text-xs text-muted-foreground">
          We aim to respond to all messages within 3–5 business days.
        </p>
      </div>
    </Shell>
  );
}
